import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AccountInput from '../components/AccountInput';

const emptyLine = () => ({ account_id: '', counter_account_id: '', debit: '', credit: '', description: '' });

// Udvid linjer med modkonti til fulde debet/kredit-par
function expandLines(lines) {
  const result = [];
  for (const line of lines) {
    if (!line.account_id || (!parseFloat(line.debit) && !parseFloat(line.credit))) continue;
    result.push({ account_id: line.account_id, debit: parseFloat(line.debit) || 0, credit: parseFloat(line.credit) || 0, description: line.description || '' });
    if (line.counter_account_id) {
      result.push({ account_id: line.counter_account_id, debit: parseFloat(line.credit) || 0, credit: parseFloat(line.debit) || 0, description: line.description || '' });
    }
  }
  return result;
}

export default function VoucherForm() {
  const { apiFetch } = useAuth();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [voucherNumber, setVoucherNumber] = useState('');
  const [description, setDescription] = useState('');
  const [lines, setLines] = useState([emptyLine(), emptyLine()]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchNextNumber = () => {
    apiFetch('/api/vouchers/next-number').then(data => {
      setVoucherNumber(String(data.next_number));
    }).catch(() => {});
  };

  useEffect(() => {
    apiFetch('/api/accounts').then(setAccounts).catch(err => setError(err.message));
    fetchNextNumber();
  }, []);

  const updateLine = (index, field, value) => {
    const updated = [...lines];
    updated[index] = { ...updated[index], [field]: value };
    setLines(updated);
  };

  const addLine = () => setLines([...lines, emptyLine()]);

  const removeLine = (index) => {
    if (lines.length <= 2) return;
    setLines(lines.filter((_, i) => i !== index));
  };

  const allLines = expandLines(lines);
  const totalDebit = allLines.reduce((sum, l) => sum + l.debit, 0);
  const totalCredit = allLines.reduce((sum, l) => sum + l.credit, 0);
  const diff = totalDebit - totalCredit;
  const isBalanced = Math.abs(diff) < 0.01;
  const hasSomeAmounts = totalDebit + totalCredit > 0;

  const addBalancingEntry = (accountId) => {
    const newLine = emptyLine();
    newLine.account_id = accountId;
    if (diff > 0) {
      newLine.credit = diff.toFixed(2);
    } else {
      newLine.debit = Math.abs(diff).toFixed(2);
    }
    setLines([...lines, newLine]);
  };

  const [successMsg, setSuccessMsg] = useState('');

  const saveVoucher = async (andContinue) => {
    setError('');
    setSuccessMsg('');

    if (!description.trim()) {
      setError('Beskrivelse er påkrævet.');
      return;
    }

    const expanded = expandLines(lines);
    if (expanded.length < 2) {
      setError('Mindst to posteringslinjer er påkrævet.');
      return;
    }

    if (!isBalanced) {
      setError('Debet og kredit stemmer ikke overens.');
      return;
    }

    setSaving(true);
    try {
      const body = { date, description, lines: expanded };
      if (voucherNumber) body.voucher_number = parseInt(voucherNumber);
      const result = await apiFetch('/api/vouchers', {
        method: 'POST',
        body
      });
      if (andContinue) {
        setSuccessMsg(`Bilag #${result.voucher_number || result.id} bogført.`);
        setDescription('');
        setLines([emptyLine(), emptyLine()]);
        setSaving(false);
        fetchNextNumber();
      } else {
        navigate(`/app/bilag/${result.id}`);
      }
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    saveVoucher(false);
  };

  const fmt = (n) => n.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div>
      <div className="page-header">
        <h1>Nyt bilag</h1>
      </div>

      {successMsg && <div className="alert alert-success">{successMsg}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="card">
          <div className="form-row">
            <div className="form-group">
              <label>Bilagsnr.</label>
              <input type="number" className="form-control" value={voucherNumber}
                onChange={e => setVoucherNumber(e.target.value)}
                min="1" step="1" placeholder="Auto" />
            </div>
            <div className="form-group">
              <label>Dato</label>
              <input type="date" className="form-control" value={date}
                onChange={e => setDate(e.target.value)} required />
            </div>
            <div className="form-group" style={{gridColumn: 'span 2'}}>
              <label>Beskrivelse</label>
              <input className="form-control" value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="F.eks. Kontingentindbetalinger januar" required />
            </div>
          </div>
        </div>

        <div className="card">
          <h3 style={{marginBottom: '0.75rem'}}>Posteringslinjer</h3>

          <div className="voucher-line voucher-line-with-counter" style={{fontWeight: 600, fontSize: '0.8rem', color: 'var(--gray-500)'}}>
            <span>Konto</span>
            <span>Debet</span>
            <span>Kredit</span>
            <span>Modkonto</span>
            <span>Beskrivelse</span>
            <span></span>
          </div>

          {lines.map((line, i) => (
            <div key={i} className="voucher-line voucher-line-with-counter">
              <AccountInput accounts={accounts} value={line.account_id}
                onChange={v => updateLine(i, 'account_id', v)}
                placeholder="Skriv kontonr..." />
              <input type="number" className="form-control" placeholder="0,00" step="0.01" min="0"
                value={line.debit} onChange={e => updateLine(i, 'debit', e.target.value)} />
              <input type="number" className="form-control" placeholder="0,00" step="0.01" min="0"
                value={line.credit} onChange={e => updateLine(i, 'credit', e.target.value)} />
              <AccountInput accounts={accounts} value={line.counter_account_id}
                onChange={v => updateLine(i, 'counter_account_id', v)}
                placeholder="Modkonto..." />
              <input className="form-control" placeholder="Valgfri beskrivelse"
                value={line.description} onChange={e => updateLine(i, 'description', e.target.value)} />
              <button type="button" className="btn btn-danger btn-sm" onClick={() => removeLine(i)}
                disabled={lines.length <= 2}>&#x2715;</button>
            </div>
          ))}

          {!isBalanced && hasSomeAmounts && (
            <div className="voucher-line voucher-line-with-counter" style={{background: '#fffbeb', borderRadius: '4px', padding: '0.25rem'}}>
              <AccountInput accounts={accounts} value=""
                onChange={v => addBalancingEntry(v)}
                placeholder="Udlign: vælg konto..." />
              <span style={{padding: '0.5rem', fontSize: '0.85rem', color: 'var(--gray-500)'}}>
                {diff < 0 ? fmt(Math.abs(diff)) : ''}
              </span>
              <span style={{padding: '0.5rem', fontSize: '0.85rem', color: 'var(--gray-500)'}}>
                {diff > 0 ? fmt(diff) : ''}
              </span>
              <span></span>
              <span style={{fontSize: '0.8rem', color: 'var(--gray-500)', padding: '0.5rem'}}>Udligning</span>
              <span></span>
            </div>
          )}

          <div className="voucher-totals voucher-totals-with-counter">
            <span>Total</span>
            <span className={isBalanced ? 'balance-ok' : 'balance-error'}>{fmt(totalDebit)}</span>
            <span className={isBalanced ? 'balance-ok' : 'balance-error'}>{fmt(totalCredit)}</span>
            <span></span>
            <span className={isBalanced ? 'balance-ok' : 'balance-error'}>
              {isBalanced ? 'I balance' : `Difference: ${fmt(Math.abs(diff))}`}
            </span>
            <span></span>
          </div>

          <button type="button" className="btn btn-secondary" onClick={addLine} style={{marginTop: '0.5rem'}}>
            + Tilføj linje
          </button>
        </div>

        <div style={{display: 'flex', gap: '0.5rem', marginTop: '1rem'}}>
          <button type="submit" className="btn btn-primary" disabled={saving || !isBalanced}>
            {saving ? 'Gemmer...' : 'Bogfør bilag'}
          </button>
          <button type="button" className="btn btn-success" disabled={saving || !isBalanced}
            onClick={() => saveVoucher(true)}>
            {saving ? 'Gemmer...' : 'Bogfør & næste bilag'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/app/bilag')}>Annuller</button>
        </div>
      </form>
    </div>
  );
}
